import { create } from 'zustand';
import { supabase } from '../supabase';
import { useDealStore } from './dealStore';
import { useServiceTaskTemplateStore } from './serviceTaskTemplateStore';
import { useDealVariableStore } from './dealVariableStore';
import type { DealService } from '../types';

interface HoursPackageAlert {
  isAlert: boolean;
  percentUsed: number;
  hoursRemaining: number;
  hoursPackage: number;
  hoursUsed: number;
}

interface DealServiceStore {
  dealServices: DealService[];
  loading: boolean;
  error: string | null;
  fetchDealServices: (dealId: string) => Promise<DealService[]>;
  fetchServiceCostPreview: (serviceId: string) => Promise<any[]>;
  createDealService: (dealService: Partial<DealService>) => Promise<DealService | null>;
  addDealService: (dealService: Partial<DealService>) => Promise<DealService | null>; // Alias for createDealService with different signature
  updateDealService: (id: string, dealService: Partial<DealService>) => Promise<DealService | null>;
  deleteDealService: (id: string) => Promise<boolean>;
  recalculateDealAmount: (dealId: string) => Promise<void>;
  // Hours package methods
  getHoursPackageServices: (dealId: string) => DealService[];
  getHoursUsagePercent: (dealServiceId: string) => number | null;
  checkHoursAlert: (dealServiceId: string) => HoursPackageAlert | null;
  updateHoursPackage: (dealServiceId: string, hoursPackage: number, hourlyRate?: number, alertThreshold?: number) => Promise<boolean>;
}

export const useDealServiceStore = create<DealServiceStore>((set, get) => ({
  dealServices: [],
  loading: false,
  error: null,

  fetchDealServices: async (dealId: string) => {
    set({ loading: true, error: null });
    try {
      console.log('🔍 Fetching deal services for deal:', dealId);
      
      const { data, error } = await supabase
        .from('deal_services')
        .select(`
          *,
          service:service_id (
            id,
            title,
            list_price,
            service_type,
            short_description,
            description,
            terms_and_conditions
          ),
          payment_method:payment_method_id (
            id,
            name,
            payment_schedule,
            payment_type
          ),
          deal_variables!deal_service_id (
            id,
            name,
            description,
            amount,
            type,
            base_amount,
            is_cost,
            is_internal_cost,
            is_vat_exempt,
            is_stamp_duty,
            is_stamp_duty_art15,
            include_in_pdf,
            is_confirmed,
            confirmed_at,
            confirmed_by,
            sort_order,
            created_at,
            updated_at
          ),
          addons:deal_service_addons (
            id,
            service_addon_id,
            name,
            description,
            amount,
            is_required,
            is_included_in_base
          )
        `)
        .eq('deal_id', dealId)
        .order('order', { ascending: true });

      if (error) {
        console.error('❌ Error fetching deal services:', error);
        throw error;
      }
      
      // DEBUG: Log raw data from database
      console.log('🔍 Raw deal services from database:', data?.map(s => ({
        id: s.id,
        service_id: s.service_id,
        deal_variables_raw: s.deal_variables,
        deal_variables_length: s.deal_variables?.length || 0
      })));
      
      // Ensure data is properly formatted - variables are now loaded directly from the query
      const formattedData = data?.map(service => ({
        ...service,
        payment_schedule: service.payment_schedule || { installments: [] },
        variables: service.deal_variables || [], // Use the variables from the query
        // Snapshot add-on della riga (deal_service_addons): DealForm li mappa in
        // editingService.addons — senza questo embed il picker in edit partiva vuoto
        addons: service.addons || []
      })) || [];
      
      set({ dealServices: formattedData, loading: false, error: null });
      return formattedData;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to fetch deal services';
      console.error('❌ Error in fetchDealServices:', errorMessage);
      set({ error: errorMessage, loading: false, dealServices: [] });
      return [];
    }
  },

  fetchServiceCostPreview: async (serviceId: string) => {
    try {
      console.log('🔍 Fetching cost templates preview for service:', serviceId);

      const { data, error } = await supabase
        .from('service_cost_templates')
        .select(`
          *,
          cost_category:cost_category_id (
            id,
            name,
            category_type
          )
        `)
        .eq('service_id', serviceId)
        .eq('status', 'pending'); // Only enabled templates

      if (error) {
        console.error('❌ Error fetching cost templates:', error);
        throw error;
      }

      console.log(`✅ Fetched ${data?.length || 0} cost templates for service`);
      return data || [];
    } catch (error: any) {
      console.error('❌ Error in fetchServiceCostPreview:', error);
      return [];
    }
  },

  recalculateDealAmount: async (dealId: string) => {
    try {
      console.log('💰 Recalculating deal amount for deal:', dealId);

      const services = get().dealServices;
      const { calculateServiceVariablesTotal } = useDealVariableStore.getState();

      let totalAmount = 0;

      // Calculate service amounts + their variables
      services.forEach(service => {
        const baseAmount = service.discounted_amount ?? service.amount;

        // 1. Base service amount
        let serviceAmount = baseAmount;
        if (service.service?.service_type === 'monthly' && service.monthly_duration) {
          serviceAmount = baseAmount * service.monthly_duration;
        }

        // 2. Add variables for this service (marche da bollo, trasferte, etc.)
        const { variablesTotal, costsTotal } = calculateServiceVariablesTotal(
          service.id,
          baseAmount  // Use base amount for percentage calculations
        );

        totalAmount += serviceAmount + variablesTotal - costsTotal;
      });

      console.log('📊 New total amount (services + variables):', totalAmount);

      // Calculate estimated internal costs from templates
      let totalEstimatedCosts = 0;
      for (const service of services) {
        if (service.service_id) {
          const costTemplates = await get().fetchServiceCostPreview(service.service_id);
          const serviceCosts = costTemplates.reduce((sum, template) => {
            return sum + (template.default_amount || 0);
          }, 0);
          totalEstimatedCosts += serviceCosts;
          console.log(`💰 Service ${service.service?.title || service.service_id}: ${serviceCosts}€ in costs`);
        }
      }

      console.log('💰 Total estimated costs:', totalEstimatedCosts);

      // Calculate margin percentage
      const marginPercentage = totalAmount > 0
        ? ((totalAmount - totalEstimatedCosts) / totalAmount) * 100
        : 0;

      console.log('📊 Estimated margin:', marginPercentage.toFixed(2) + '%');

      const { updateDeal, fetchDealById } = useDealStore.getState();
      await updateDeal(dealId, {
        amount: totalAmount,
        internal_cost_estimate: totalEstimatedCosts,
        estimated_margin_percentage: marginPercentage
      });

      console.log('✅ Deal amount, costs, and margin updated in database');

      // Refetch the deal to ensure the list and dashboard show the updated amount
      await fetchDealById(dealId);
      console.log('✅ Deal refetched to update list and dashboard');
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to recalculate deal amount';
      console.error('❌ Error recalculating deal amount:', errorMessage);
      set({ error: errorMessage });
    }
  },

  createDealService: async (dealService: Partial<DealService>) => {
    try {
      console.log('➕ Creating new deal service - Input data:', dealService);
      console.log('🔍 Metadata received:', dealService.metadata);

      // Get current max order value
      const { data: maxOrderData } = await supabase
        .from('deal_services')
        .select('order')
        .eq('deal_id', dealService.deal_id)
        .order('order', { ascending: false })
        .limit(1);

      const nextOrder = maxOrderData && maxOrderData.length > 0
        ? (maxOrderData[0].order || 0) + 1
        : 0;

      // Fetch service to get hours package config (if applicable)
      let hoursPackageConfig = {
        is_hours_package: false,
        hours_package: 0,
        hourly_rate: null as number | null,
        hours_alert_threshold: 80,
        hours_used: 0
      };

      if (dealService.service_id) {
        const { data: serviceData, error: serviceError } = await supabase
          .from('services')
          .select('is_hours_package, hours_package_default, hours_package_hourly_rate, hours_package_alert_threshold')
          .eq('id', dealService.service_id)
          .single();

        if (!serviceError && serviceData?.is_hours_package) {
          console.log('📦 Service is hours package, copying config:', serviceData);
          hoursPackageConfig = {
            is_hours_package: true,
            hours_package: serviceData.hours_package_default || 0,
            hourly_rate: serviceData.hours_package_hourly_rate || null,
            hours_alert_threshold: serviceData.hours_package_alert_threshold || 80,
            hours_used: 0
          };
        }
      }

      // Log payment schedule data before processing
      console.log('📅 ADD - Raw payment schedule data:', dealService.payment_schedule);
      console.log('📅 ADD - Payment schedule type:', typeof dealService.payment_schedule);
      console.log('📅 ADD - Is array:', Array.isArray(dealService.payment_schedule));

      // Prepare payment schedule data for database
      // payment_schedule should now always have correct format
      const paymentScheduleData = dealService.payment_schedule
        ? {
            installments: (dealService.payment_schedule.installments || []).map(inst => ({
              type: inst.type || 'fixed',
              value: inst.value || 0,
              amount: inst.amount || 0,
              daysAfterStart: inst.daysAfterStart || 0,
              description: inst.description || '',
              isConfigurable: inst.isConfigurable || false,
              timeUnit: inst.timeUnit || 'days',
              fixedDayOfMonth: inst.fixedDayOfMonth, // Giorno fisso del mese (opzionale)
              nextMonth: inst.nextMonth // Prossimo mese (opzionale)
            }))
          }
        : null;

      console.log('📅 Processed payment schedule data:', paymentScheduleData);

      // Verify total amount matches service amount
      if (paymentScheduleData) {
        const totalScheduled = paymentScheduleData.installments.reduce(
          (sum, inst) => sum + (inst.amount || 0),
          0
        );
        const serviceAmount = dealService.discounted_amount ?? dealService.amount;
        
        console.log('🔍 Validating payment schedule:', {
          totalScheduled,
          serviceAmount,
          difference: Math.abs(totalScheduled - serviceAmount)
        });
        
        if (Math.abs(totalScheduled - serviceAmount) > 0.01) {
          throw new Error('Payment schedule total does not match service amount');
        }
      }

      // Prepare complete insert data
      const isProductLine = (dealService as any).line_type === 'product';
      const insertData = {
        deal_id: dealService.deal_id,
        // Per le righe prodotto magazzino service_id è un sintetico (es. "product-{uuid}-{ts}") non valido come FK → passa NULL
        service_id: isProductLine ? null : dealService.service_id,
        payment_method_id: dealService.payment_method_id,
        amount: dealService.amount,
        discounted_amount: dealService.discounted_amount,
        description: dealService.description,
        payment_schedule: paymentScheduleData,
        monthly_duration: dealService.monthly_duration,
        custom_service_name: dealService.custom_service_name,
        // Categoria della riga libera: senza, il cliente resta fuori dalle segmentazioni per acquisto.
        category_id: (dealService as any).category_id ?? null,
        metadata: dealService.metadata,
        order: nextOrder,
        // NB: override_commission_scaling NON va inviata qui — la colonna è stata rimossa da
        // deal_services col cutover provvigioni V2 (P12) e vive solo su deals. Includerla nel
        // payload farebbe rispondere PostgREST con PGRST204.
        // Hours package fields (copied from service template)
        is_hours_package: hoursPackageConfig.is_hours_package,
        hours_package: hoursPackageConfig.hours_package,
        hours_used: hoursPackageConfig.hours_used,
        hourly_rate: hoursPackageConfig.hourly_rate,
        hours_alert_threshold: hoursPackageConfig.hours_alert_threshold,
        // VIVA: righe prodotto magazzino — colonne dedicate (line_type ha default 'service' nel DB)
        line_type: (dealService as any).line_type ?? 'service',
        product_id: (dealService as any).product_id ?? null,
        quantity: (dealService as any).quantity ?? null,
        unit_of_measure: (dealService as any).unit_of_measure ?? null,
        unit_price: (dealService as any).unit_price ?? null,
        // IVA per-riga: null = eredita da products/services.vat_rate (fallback 22)
        vat_rate: (dealService as any).vat_rate ?? null,
      };

      console.log('📝 Final data being sent to database:', insertData);

      // Insert deal service with payment schedule
      const { data, error } = await supabase
        .from('deal_services')
        .insert(insertData)
        .select(`
          *,
          service:service_id (
            id,
            title,
            list_price,
            service_type,
            short_description,
            description,
            terms_and_conditions
          ),
          payment_method:payment_method_id (
            id,
            name,
            payment_schedule,
            payment_type
          )
        `)
        .single();

      if (error) {
        terminal.error('❌ Error creating deal service:', error);
        throw error;
      }
      
      console.log('✅ Deal service created successfully - Response data:', data);
      
      const dealServices = get().dealServices;
      set({ dealServices: [...dealServices, data] });
      
      // Recalculate deal amount
      await get().recalculateDealAmount(dealService.deal_id);
      
      // Create tasks from templates automatically
      try {
        const { createTasksFromTemplates } = useServiceTaskTemplateStore.getState();
        const tasksCreated = await createTasksFromTemplates(
          dealService.deal_id, 
          data.id, 
          dealService.service_id, 
          'service_added'
        );
        
        if (tasksCreated > 0) {
          console.log(`✅ Created ${tasksCreated} tasks from templates for deal service`);
        }
      } catch (taskError) {
        console.warn('⚠️ Failed to create tasks from templates:', taskError);
        // Non-blocking error - the deal service was still created successfully
      }
      
      // Save variables for this service if they exist
      if (dealService.variables && dealService.variables.length > 0) {
        try {
          const { useDealVariableStore } = await import('./dealVariableStore');
          const { createServiceVariable } = useDealVariableStore.getState();
          
          console.log('💾 Saving variables for service:', data.id, dealService.variables);
          
          for (const variable of dealService.variables) {
            console.log('🔍 Processing variable:', variable.name, 'ID:', variable.id);

            // Skip variables that already have a real database ID (not temporary)
            // Accept both temp- (manually added) and template- (from service templates)
            const isTemp = variable.id?.startsWith('temp-');
            const isTemplate = variable.id?.startsWith('template-');

            if (!isTemp && !isTemplate) {
              console.log('⚠️ Skipping variable with real ID during service creation:', variable.id);
              continue;
            }

            console.log('📝 Saving variable (isTemp:', isTemp, ', isTemplate:', isTemplate, '):', variable.name);

            // Create new variable in database
            const variableData = {
              name: variable.name,
              description: variable.description || '',
              amount: variable.amount ?? 0,
              type: variable.type || 'fixed',
              is_cost: variable.is_cost ?? false,
              is_vat_exempt: variable.is_vat_exempt ?? false,
              is_stamp_duty: variable.is_stamp_duty ?? false,
              is_stamp_duty_art15: variable.is_stamp_duty_art15 ?? false,
              is_internal_cost: variable.is_internal_cost ?? false,
              sort_order: variable.sort_order ?? 0
            };

            await createServiceVariable(data.id, variableData);
            console.log('✅ Variable saved successfully:', variable.name);
          }
        } catch (error) {
          console.error('❌ Error saving variables for service:', error);
          // Non-blocking error - the deal service was still created successfully
        }
      }
      
      return data;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to create deal service';
      terminal.error('❌ Error in createDealService:', errorMessage);
      set({ error: errorMessage });
      return null;
    }
  },

  updateDealService: async (id: string, dealService: Partial<DealService>) => {
    try {
      console.log('🔄 Updating deal service - Input data:', { id, updates: dealService });

      // Log payment schedule data before processing
      console.log('📅 UPDATE - Raw payment schedule data:', dealService.payment_schedule);
      console.log('📅 UPDATE - Payment schedule type:', typeof dealService.payment_schedule);
      console.log('📅 UPDATE - Is array:', Array.isArray(dealService.payment_schedule));

      // Prepare payment schedule data for database
      // payment_schedule should now always have correct format
      const paymentScheduleData = dealService.payment_schedule
        ? {
            installments: (dealService.payment_schedule.installments || []).map(inst => ({
              type: inst.type || 'fixed',
              value: inst.value || 0,
              amount: inst.amount || 0,
              daysAfterStart: inst.daysAfterStart || 0,
              description: inst.description || '',
              isConfigurable: inst.isConfigurable || false,
              timeUnit: inst.timeUnit || 'days',
              fixedDayOfMonth: inst.fixedDayOfMonth, // Giorno fisso del mese (opzionale)
              nextMonth: inst.nextMonth // Prossimo mese (opzionale)
            }))
          }
        : undefined;

      console.log('📅 Processed payment schedule data:', paymentScheduleData);

      // Verify total amount matches service amount if payment schedule is provided
      if (paymentScheduleData) {
        const totalScheduled = paymentScheduleData.installments.reduce(
          (sum, inst) => sum + (inst.amount || 0),
          0
        );
        const serviceAmount = dealService.discounted_amount ?? dealService.amount ??
          get().dealServices.find(s => s.id === id)?.amount ?? 0;
        
        console.log('🔍 Validating payment schedule:', {
          totalScheduled,
          serviceAmount,
          difference: Math.abs(totalScheduled - serviceAmount)
        });
        
        if (Math.abs(totalScheduled - serviceAmount) > 0.01) {
          throw new Error('Payment schedule total does not match service amount');
        }
      }

      // Prepare complete update data
      const isProductLineUpd = (dealService as any).line_type === 'product';
      const updateData = {
        // Righe prodotto: service_id deve restare NULL (sintetico non FK-valido)
        service_id: isProductLineUpd ? null : dealService.service_id,
        payment_method_id: dealService.payment_method_id,
        amount: dealService.amount,
        discounted_amount: dealService.discounted_amount,
        description: dealService.description,
        payment_schedule: paymentScheduleData,
        monthly_duration: dealService.monthly_duration,
        custom_service_name: dealService.custom_service_name,
        // Categoria della riga libera (vedi createDealService)
        category_id: (dealService as any).category_id ?? null,
        metadata: dealService.metadata,
        order: dealService.order,
        // override_commission_scaling: rimossa da deal_services col cutover V2 (vedi addDealService)
        // VIVA: righe prodotto magazzino
        line_type: (dealService as any).line_type ?? 'service',
        product_id: (dealService as any).product_id ?? null,
        quantity: (dealService as any).quantity ?? null,
        unit_of_measure: (dealService as any).unit_of_measure ?? null,
        unit_price: (dealService as any).unit_price ?? null,
        // IVA per-riga: null = eredita da products/services.vat_rate (fallback 22)
        vat_rate: (dealService as any).vat_rate ?? null,
      };

      console.log('📝 Final data being sent to database:', updateData);

      // Update deal service with payment schedule
      const { data, error } = await supabase
        .from('deal_services')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          service:service_id (
            id,
            title,
            list_price,
            service_type,
            short_description,
            description,
            terms_and_conditions
          ),
          payment_method:payment_method_id (
            id,
            name,
            payment_schedule,
            payment_type
          )
        `)
        .single();

      if (error) {
        terminal.error('❌ Error updating deal service:', error);
        throw error;
      }
      
      console.log('✅ Deal service updated successfully - Response data:', data);

      const dealServices = get().dealServices.map(ds => 
        ds.id === id ? { ...ds, ...data } : ds
      );
      
      set({ dealServices });
      
      // Recalculate deal amount
      const currentService = get().dealServices.find(s => s.id === id);
      if (currentService?.deal_id) {
        await get().recalculateDealAmount(currentService.deal_id);
      }
      
      return data;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to update deal service';
      terminal.error('❌ Error in updateDealService:', errorMessage);
      set({ error: errorMessage });
      return null;
    }
  },

  addDealService: async (dealService: Partial<DealService>) => {
    // This is an alias/wrapper for createDealService with same signature
    return get().createDealService(dealService);
  },

  deleteDealService: async (id: string) => {
    try {
      console.log('🗑️ Deleting deal service:', id);

      // Get the deal ID before deleting the service
      const service = get().dealServices.find(ds => ds.id === id);
      if (!service) {
        terminal.error('❌ Service not found:', id);
        throw new Error('Service not found');
      }

      const dealId = service.deal_id;

      const { error } = await supabase
        .from('deal_services')
        .delete()
        .eq('id', id);

      if (error) {
        terminal.error('❌ Error deleting deal service:', error);
        throw error;
      }

      console.log('✅ Deal service deleted successfully');

      const dealServices = get().dealServices.filter(ds => ds.id !== id);
      set({ dealServices });

      // Recalculate deal amount
      await get().recalculateDealAmount(dealId);

      return true;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to delete deal service';
      terminal.error('❌ Error in deleteDealService:', errorMessage);
      set({ error: errorMessage });
      return false;
    }
  },

  // =====================================================
  // HOURS PACKAGE METHODS
  // =====================================================

  getHoursPackageServices: (dealId: string) => {
    const dealServices = get().dealServices;
    return dealServices.filter(ds => ds.deal_id === dealId && ds.is_hours_package === true);
  },

  getHoursUsagePercent: (dealServiceId: string) => {
    const ds = get().dealServices.find(d => d.id === dealServiceId);
    if (!ds?.is_hours_package || !ds.hours_package || ds.hours_package === 0) {
      return null;
    }
    return ((ds.hours_used || 0) / ds.hours_package) * 100;
  },

  checkHoursAlert: (dealServiceId: string) => {
    const ds = get().dealServices.find(d => d.id === dealServiceId);
    if (!ds?.is_hours_package || !ds.hours_package || ds.hours_package === 0) {
      return null;
    }

    const hoursUsed = ds.hours_used || 0;
    const hoursPackage = ds.hours_package;
    const alertThreshold = ds.hours_alert_threshold || 80;
    const percentUsed = (hoursUsed / hoursPackage) * 100;

    return {
      isAlert: percentUsed >= alertThreshold,
      percentUsed,
      hoursRemaining: hoursPackage - hoursUsed,
      hoursPackage,
      hoursUsed
    };
  },

  updateHoursPackage: async (dealServiceId: string, hoursPackage: number, hourlyRate?: number, alertThreshold?: number) => {
    try {
      console.log('📦 Updating hours package for deal service:', dealServiceId);

      const updateData: Record<string, any> = {
        hours_package: hoursPackage
      };

      if (hourlyRate !== undefined) {
        updateData.hourly_rate = hourlyRate;
      }

      if (alertThreshold !== undefined) {
        updateData.hours_alert_threshold = alertThreshold;
      }

      const { error } = await supabase
        .from('deal_services')
        .update(updateData)
        .eq('id', dealServiceId);

      if (error) {
        console.error('❌ Error updating hours package:', error);
        throw error;
      }

      // Update local state
      const dealServices = get().dealServices.map(ds =>
        ds.id === dealServiceId
          ? { ...ds, ...updateData }
          : ds
      );
      set({ dealServices });

      console.log('✅ Hours package updated successfully');
      return true;
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to update hours package';
      console.error('❌ Error in updateHoursPackage:', errorMessage);
      set({ error: errorMessage });
      return false;
    }
  },
}));